import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

/**
 * §11.13 Evening 7B — the operator incident dashboard's data layer. `GET /admin/incidents`
 * is polled (incidents arrive asynchronously — the detector ticks every 60s, the auto-approve
 * sweep every 60s), matching the admin-alerts 30s cadence. Approve / deny / close invalidate
 * the list so the screen reflects the new state immediately.
 */

/** A unit taken OFFLINE by Layer 4, with its trigger + recent-failure diagnostics. */
export type SuspendedUnit = {
  unitId: string;
  unitName: string;
  triggerOutageId: string | null;
  suspendedAt: string | null;
  lastFailures: string[];
};

export type PausedLocation = {
  locationId: string;
  locationName: string;
  openedAt: string | null;
};

/** A compensation decided but held for operator approval (approval mode ON). */
export type QueuedCompensation = {
  outageId: string;
  bookingId: string | null;
  decisionAction: string;
  refundAmount: string | null;
  creditAmount: string | null;
  queuedAt: string;
  /** Absolute instant (server-computed queuedAt + the tenant's actual timeout) the UI ticks down to. */
  autoApproveDeadline: string;
};

/** A denied compensation awaiting explicit operator close-out (the manual work queue). */
export type PendingManualResolution = {
  outageId: string;
  bookingId: string | null;
  decisionAction: string;
  reason: string | null;
  deniedBy: string | null;
  deniedAt: string | null;
};

export type IncidentsResponse = {
  suspendedUnits: SuspendedUnit[];
  pausedLocations: PausedLocation[];
  queuedCompensations: QueuedCompensation[];
  pendingManualResolution: PendingManualResolution[];
};

export function useIncidents() {
  return useQuery({
    queryKey: ['adminIncidents'],
    queryFn: () => apiClient<IncidentsResponse>('/admin/incidents'),
    refetchInterval: 30_000,
  });
}

export function useApproveIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (outageId: string) =>
      apiClient<{ status: string }>(`/admin/incidents/${outageId}/approve`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminIncidents'] });
    },
  });
}

export function useDenyIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outageId, reason }: { outageId: string; reason: string }) =>
      apiClient<{ status: string }>(`/admin/incidents/${outageId}/deny`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminIncidents'] });
    },
  });
}

export function useCloseIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (outageId: string) =>
      apiClient<{ status: string }>(`/admin/incidents/${outageId}/close`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminIncidents'] });
    },
  });
}

// useRestoreUnit lives in useAdminUnits.ts (units-domain action, shared with this dashboard) — it
// invalidates the incident list too, so the paused/suspended surfaces both refresh on restore.

/**
 * Resume a paused facility (Layer 5). Facility incidents no longer auto-unpause — resumption is an
 * operator decision once the outage has cleared. Hits the manual-unpause endpoint, then refreshes
 * the incident list so the paused location leaves the active-incidents section.
 */
export function useResumeLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) =>
      apiClient<{ status: string }>(`/admin/incidents/locations/${locationId}/resume`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminIncidents'] });
    },
  });
}
